<template>
  <v-container fluid>
    <v-toolbar class="pb-10" flat>
      <DebouncedTextField
        v-model="search"
        append-icon="mdi-magnify"
        class="d-print-none"
        clearable
        hide-details
        label="Search Tasks"
        single-line
      />
      <v-spacer/>

      <v-spacer/>
      <v-text-field :value="datesFormatted"
                    append-icon="mdi-calendar"
                    hint="Task Dates"
                    label="Select dates"
                    persistent-hint
                    readonly
                    single-line
                    @click="showDatePicker=true"
      />

      <v-dialog
        ref="dialog"
        v-model="showDatePicker"
        persistent
        width="290px"
      >
        <v-date-picker
          v-model="dates"
          no-title
          range
        ></v-date-picker>
        <v-btn @click="showDatePicker=false; (dates||[]).sort()">Close</v-btn>
      </v-dialog>

    </v-toolbar>

    <v-row>
      <!-- Task Selection -->
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Select Task for Analysis
            <v-spacer/>
            <v-btn
              :disabled="tasksWithoutRatesCount === 0"
              @click="setAndSaveRatesFromApi"
            >
              Set Rates For Tasks Without Rates ({{ tasksWithoutRatesCount }})
            </v-btn>
          </v-card-title>
          <v-card-text>
            <ChoppingTaskTable
              :headers="taskHeaders"
              :loading="loading"
              :search="search"
              :tasks="filteredTasks"
              :teams="teams"
              clickable
              @row-click="(item, event) => selectTask(item, event)"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from '@/api';
import moment from 'moment';
import urlState from "../router/urlState";
import ChoppingTaskTable from '@/components/ChoppingTaskTable.vue';
import DebouncedTextField from "@/components/DebouncedTextField.vue";

export default {
  name: "TaskList",
  mixins: [urlState],
  components: {
    ChoppingTaskTable,
    DebouncedTextField
  },
  data() {
    return {
      loading: false,
      showDatePicker: null,
      dates: [],
      search: null,
      selectedTeam: '',
      teamFilter: [],
      rawTasks: [],
      teams: {},
      taskHeaders: [
        {text: 'Task', value: 'title'},
        {text: 'Date', value: 'date'},
        {text: 'Task Times', value: 'executionTimes'},
        {text: 'Total Weight', value: 'totalWeight'},
        {text: 'Duration', value: 'duration'},
        {text: 'Actual Rate (kg/hr)', value: 'rate'},
        {text: 'Estimated Duration', value: 'estimatedDuration'},
        {text: 'Estimated Rate (kg/hr)', value: 'apiRate'},
        {text: 'Performance', value: 'performance'},
        {text: 'Description', value: 'description'},
      ]
    }
  },
  async mounted() {
    this.syncToUrl({
      param: 'dates', urlParam: 'dates',
      initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'selectedTeam', urlParam: 'team', initFromRoute: true
    });
    await this.$nextTick();
    if (this.dates && this.dates.sort)
      this.dates.sort();
    await this.updateDateRange();
  },
  watch: {
    dates: 'updateDateRange'
  },
  computed: {
    datesFormatted() {
      const format = 'dddd MMMM D';
      const from = this.dates[0];
      const to = this.dates[1];
      if (!from) {
        return '';
      } else {
        if (from === to) {
          return `${moment(from).format(format)}`;
        } else {
          return `${moment(from).format(format)} - ${moment(to).format(format)}`
        }
      }
    },
    filteredTasks() {
      // Since we're only loading chopping team tasks, no need to filter by team
      return this.rawTasks;
    },

    tasksWithoutRatesCount() {
      if (!this.rawTasks) return 0;
      return this.rawTasks.filter(task => {
        const foodId = task.source ? task.source.foodid : null;
        return foodId && (!task.apiRate || task.apiRate === null || task.apiRate === 'N/A');
      }).length;
    }
  },
  methods: {
    selectAllMembers() {
      this.selectedMembers = [];
    },
    async fetchTeamData() {
      const knownTeams = ['chopping', 'components', 'packing', 'plating'];
      const teamPromises = knownTeams.map(async (teamName) => {
        try {
          const response = await api.get(`/v2/task/team/${teamName}`);
          return {id: response.data.id, name: teamName};
        } catch (error) {
          console.warn(`Failed to fetch team ${teamName}:`, error);
          return null;
        }
      });

      const teamResults = await Promise.all(teamPromises);
      const teams = {};

      teamResults.forEach(team => {
        if (team) {
          teams[team.id] = team.name;
        }
      });

      this.teams = teams;
      return teams;
    },
    async updateDateRange() {
      const [from, to] = [...this.dates].sort();
      if (from && to) {
        if (Object.keys(this.teams).length === 0) {
          await this.fetchTeamData();
        }

        // Get chopping team ID
        const choppingTeamId = Object.keys(this.teams).find(id => this.teams[id] === 'chopping');

        this.loading = true;
        return api
          .get(`/v2/task`, {
            params: {
              from,
              to,
              team: choppingTeamId,
              includeProcessingRates: true,
              queryByExecution: true
            }
          })
          .then(async result => {
            const {data: tasks} = result;
            this.rawTasks = tasks;

            // No need to extract team names since we're only showing chopping team tasks
            this.teamFilter = ['chopping'];
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('MMM D, YYYY');
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).format('MMM D, YYYY [at] HH:mm');
    },

    cleanTaskTitle(title) {
      if (!title) return title;
      // Remove patterns like (P1), (P2), (PR), etc.
      return title.replace(/\s*\([A-Z]\d*\)\s*/g, '').trim();
    },

    async selectTask(task) {
      console.log('selecting task for analysis');
      const foodId = task.source ? task.source.foodid : null;

      if (foodId) {
        // Navigate to the task analysis page
        this.$router.push({
          path: `/task-analysis/chopping/${foodId}`,
          query: {
            dates: this.dates,
            team: 'chopping'
          }
        });
      }
    },

    // Combined method to set and save rates for tasks without existing rates
    async setAndSaveRatesFromApi() {
      if (!this.rawTasks || this.rawTasks.length === 0) {
        console.warn('No tasks available to process');
        return;
      }

      const tasksWithoutRates = this.rawTasks.filter(task => {
        const foodId = task.source ? task.source.foodid : null;
        return foodId && (!task.apiRate || task.apiRate === null || task.apiRate === 'N/A');
      });

      if (tasksWithoutRates.length === 0) {
        console.log('No tasks without rates found');
        return;
      }

      this.loading = true;
      let processedCount = 0;
      let savedCount = 0;
      let errorCount = 0;

      try {
        console.log(`Processing ${tasksWithoutRates.length} tasks without rates...`);

        for (const task of tasksWithoutRates) {
          const foodId = task.source.foodid;

          try {
            // First, get the median rate from the API
            const rateResponse = await api.get(`/v2/task/median-rate/${foodId}`);
            const estimateData = rateResponse.data;

            if (estimateData && estimateData.medianRate && estimateData.medianRate > 0) {
              const medianRate = estimateData.medianRate;
              processedCount++;

              // Save the rate to the processing rate endpoint
              try {
                await api.post(`/v2/food/processing-rate/${foodId}`, {
                  entry_type: 'api-estimate',
                  fixed_hours: 0,
                  piecewise_linear_data: [
                    {
                      weight_kg: 1,
                      rate: medianRate // Save as kg/hr
                    }
                  ]
                });

                // Update the task's apiRate immediately
                task.apiRate = medianRate.toFixed(2);
                savedCount++;

                console.log(`Set and saved processing rate for food ID ${foodId}: ${medianRate.toFixed(2)} kg/hr`);
              } catch (saveError) {
                console.error(`Failed to save processing rate for food ID ${foodId}:`, saveError);
                errorCount++;
              }
            }
          } catch (fetchError) {
            console.warn(`Failed to fetch median rate for food ID ${foodId}:`, fetchError);
            errorCount++;
          }
        }

        const message = `Processed ${processedCount} rates, saved ${savedCount} successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`;
        console.log(message);

        // Reload the tasks to reflect the updated processing rates
        if (savedCount > 0) {
          await this.updateDateRange();
        }

      } catch (error) {
        console.error('Failed to set and save rates from API:', error);
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style>
.row-hover tbody tr:hover {
  background-color: #f5f5f5 !important;
  cursor: pointer;
}
</style>